import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Transaction } from '../api/types';
import { dayHeading, formatEuro } from '../lib/format';
import { useAddSheet } from './AddSheet';
import { Dot } from './ui';
import { TransactionTable } from './TransactionTable';

/**
 * Riga di movimento con gesti: si tocca per modificare, si trascina a destra
 * per "Ripeti" (re-inserisce oggi lo stesso importo) e a sinistra per
 * eliminare. Le stesse due azioni restano raggiungibili con i pulsanti che
 * compaiono al passaggio del mouse, cosi' su desktop e con tastiera non
 * serve trascinare.
 */
const THRESHOLD = 72;

/** Ripeti ed elimina, condivisi dalla riga mobile e dalla tabella desktop. */
export function useTransactionActions(transaction: Transaction) {
  const queryClient = useQueryClient();
  const addSheet = useAddSheet();

  const remove = useMutation({
    mutationFn: () => api.delete(`/transactions/${transaction.id}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  function repeat() {
    addSheet.open({
      amount: transaction.amount,
      categoryId: transaction.category.id,
      kind: transaction.kind,
      note: transaction.note,
    });
  }

  return { repeat, remove: () => remove.mutate() };
}

export function TransactionRow({
  transaction,
  first = false,
}: {
  transaction: Transaction;
  first?: boolean;
}) {
  const addSheet = useAddSheet();
  const { repeat, remove } = useTransactionActions(transaction);
  const [offset, setOffset] = useState(0);
  const startX = useRef<number | null>(null);

  const isIncome = transaction.kind === 'INCOME';

  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-y-0 left-0 flex items-center pl-4 text-sm font-medium text-success">
        Ripeti
      </div>
      <div className="absolute inset-y-0 right-0 flex items-center pr-4 text-sm font-medium text-danger">
        Elimina
      </div>

      <div
        className="group relative flex items-center gap-3 bg-surface px-4 transition-transform"
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={(e) => {
          startX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchMove={(e) => {
          if (startX.current === null) return;
          setOffset((e.touches[0]?.clientX ?? 0) - startX.current);
        }}
        onTouchEnd={() => {
          if (offset > THRESHOLD) repeat();
          else if (offset < -THRESHOLD) remove();
          setOffset(0);
          startX.current = null;
        }}
      >
        <button
          type="button"
          onClick={() => addSheet.edit(transaction)}
          className={`flex min-h-10 min-w-0 flex-1 items-center gap-3 py-1.5 text-left ${first ? '' : 'border-t border-border'}`}
        >
          <Dot color={transaction.category.color} small />
          <span className="min-w-0 flex-1 truncate text-[0.9375rem]">
            {transaction.category.name}
            {transaction.note && <span className="text-muted"> · {transaction.note}</span>}
          </span>
          <span
            className={`tnum shrink-0 text-[0.9375rem] font-semibold ${isIncome ? 'text-success' : ''}`}
          >
            {isIncome ? '+' : '−'}
            {formatEuro(transaction.amount)}
          </span>
        </button>

        <span className="absolute inset-y-0 right-2 flex items-center gap-1 bg-surface pl-3 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            onClick={repeat}
            aria-label={`Ripeti ${transaction.category.name}`}
            className="rounded-md px-2 py-1 text-xs text-accent hover:bg-surface-2"
          >
            Ripeti
          </button>
          <button
            type="button"
            onClick={remove}
            aria-label={`Elimina ${transaction.category.name}`}
            className="rounded-md px-2 py-1 text-xs text-danger hover:bg-danger-soft"
          >
            Elimina
          </button>
        </span>
      </div>
    </div>
  );
}

/**
 * Movimenti raggruppati per giorno (Oggi / Ieri / data) su telefono,
 * tabella unica da 1024px in su.
 */
export function TransactionTimeline({ transactions }: { transactions: Transaction[] }) {
  const groups = new Map<string, Transaction[]>();
  for (const t of transactions) {
    const list = groups.get(t.date) ?? [];
    list.push(t);
    groups.set(t.date, list);
  }

  return (
    <>
      <div className="flex flex-col gap-4 lg:hidden">
        {[...groups.entries()].map(([date, items]) => (
          <DayGroup key={date} date={date} items={items} />
        ))}
      </div>
      <div className="hidden lg:block">
        <TransactionTable transactions={transactions} />
      </div>
    </>
  );
}

function DayGroup({ date, items }: { date: string; items: Transaction[] }) {
  const dayTotal = items.reduce((sum, t) => sum + (t.kind === 'EXPENSE' ? t.amount : -t.amount), 0);

  return (
    <section>
      <header className="mb-1 flex items-baseline justify-between px-4 text-xs text-muted">
        <h3>{dayHeading(date)}</h3>
        <span className="tnum">{formatEuro(dayTotal)}</span>
      </header>
      <div className="-mx-4 overflow-hidden border-y border-border bg-surface">
        {items.map((t, i) => (
          <TransactionRow key={t.id} transaction={t} first={i === 0} />
        ))}
      </div>
    </section>
  );
}
